import type { Pt } from "../types";
import { t } from "../../i18n";
import type { SolvedPlan, SolverRoom, SolverRampInt } from "../client";
import { pickAttrs, type PickAttrs, type Selection } from "../inspect";
import { Deck, Bay, AdaAisle, RampWallView, RoomsLayer, RoadSurface, RampIntView, AccessRampView, StallNumber } from "./glyphs";

export interface PlanViewProps {
  boundary: Pt[];
  plan: SolvedPlan;
  rooms: SolverRoom[];
  rampInt?: SolverRampInt | null;
  level?: number;
  showNumbers?: boolean;
  /** Rampele: cele de acces ale planului de situație și cea dintre
   *  niveluri. Strat propriu fiindcă ele sunt singurele lucruri de pe
   *  planșa de parcaj care nu sunt ale nivelului desenat — o rampă traversează
   *  două cote —, iar când te uiți la cum s-au așezat locurile în jurul
   *  stâlpilor ele acoperă tocmai fâșia despre care e întrebarea. Implicit
   *  aprinse: fac parte din desenul de parcaj. */
  /** Bifele celor două rampe (v. straturile `rampe_acces` / `rampe_interior`);
   *  lipsă = aprinse, rampele fac parte din desenul de parcaj. */
  showAccessRamps?: boolean;
  showRampInt?: boolean;
  /** Corpul aleilor (5 m lățime), într-un gri mai închis — stratul `alei`. */
  showLaneBodies?: boolean;
  // `interactive` marks every shape with what it IS, so the Editor can name
  // whatever the user presses. Leave it off for a static plan: the PDF export
  // renders through `renderToStaticMarkup`, and the marks would land in the
  // exported SVG as inert attributes and cursor styles.
  //
  // The selection is resolved by the EDITOR, from the `data-pick` attribute
  // this leaves on each shape — never by a click handler here. A handler needs
  // the pointerdown stopped to survive the SVG's pointer capture, and stopping
  // it also stops panning and measuring. See `inspect.pickAttrs`.
  interactive?: boolean;
  selected?: Selection | null;
}

export function PlanView(p: PlanViewProps) {
  if (p.boundary.length < 3) return null;
  const lotPts = p.boundary.map((q) => `${q[0]},${q[1]}`).join(" ");
  const lvl = p.level ?? -1;
  // ID-UL DECUPĂRII, UNIC PE PLANȘEU. Cu «toate corpurile» se desenează mai
  // multe subsoluri deodată, fiecare cu `<clipPath>`-ul lui; cu același id pe
  // toate, SVG-ul lua primul din document, și aleile și rampele celorlalte
  // subsoluri erau tăiate la conturul unui subsol străin — pe un ansamblu cu
  // trei subsoluri se vedeau locurile, dar nu și aleile (Oana, 05.10.2026).
  // Determinist (nu `useId`): planșa se randează și static, pentru PDF.
  const clipId = `pg-lot-clip-${lvl}-${Math.round(p.boundary[0][0])}-${Math.round(p.boundary[0][1])}`;
  // A selection belongs to ONE level — and, with several basements at the same
  // level, to ONE storey. Without this guard a stall picked on −1 stays
  // outlined at the same index on −2, or on the other basement, where it is
  // a different stall. Fiecare formă apăsată își spune planșeul (`storey`).
  const sel = p.selected && p.selected.level === lvl
    && (!("storey" in p.selected) || !p.selected.storey || p.selected.storey === p.plan.storey)
    ? p.selected : null;
  const st = p.plan.storey;
  const at = (kind: Selection["kind"], i: number) =>
    // `sel.i` read optionally: a `storey` selection has no index, and it never
    // matches a drawn shape — it means "this whole floor".
    sel !== null && sel.kind === kind && (sel as { i?: number }).i === i;
  // The `data-pick` attributes a shape carries so the Editor can identify it
  // when pressed. Empty when the caller wants a static plan, which is what
  // keeps the PDF export's SVG free of inert attributes and cursor styles.
  const pick = (s: Selection): PickAttrs => p.interactive ? pickAttrs(s) : {};
  const accesVizibil = p.showAccessRamps !== false;
  const interiorVizibil = p.showRampInt !== false;
  return (
    <g>
      <Deck boundary={p.boundary} />
      {/* Road surfaces and the ramp corridor are clipped to the lot outline so
          the grey never paints past the boundary. */}
      <clipPath id={clipId}><polygon points={lotPts} /></clipPath>
      <g clipPath={`url(#${clipId})`}>
        <RoadSurface deck={p.plan.deck} lanes={p.plan.lanes ?? []} />
        {/* CORPUL aleilor, peste platelaj și sub axe: lățimea din document
            (`w`, 6 m dublu sens), 5 m când documentul n-o spune; capete drepte
            (un capăt rotund ar ieși peste contur). */}
        {p.showLaneBodies !== false && (p.plan.lanes ?? []).map((l, i) => (
          <line key={`lb${i}`} x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2}
            stroke={at("lane", i) ? "var(--sel-fill)" : "var(--aisle-body)"}
            strokeWidth={l.w ?? 5} strokeLinecap="butt"
            {...pick({ kind: "lane", level: lvl, i, storey: st })} />
        ))}
        {/* AXELE aleilor, peste suprafață. Aleea e în document o axă (`Lane`),
            nu o suprafață; suprafața gri e unirea lor, iar pe un plan cu multe
            alei ea acoperă aproape tot și nu mai spune nimic despre rețea. Axa
            întreruptă arată câte alei sunt și pe unde trec — ce trebuie văzut
            când se depanează parcarea. */}
        {(p.plan.lanes ?? []).map((l, i) => (
          <line key={`lx${i}`} x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2}
            stroke="var(--ink)" strokeWidth={1} strokeOpacity={0.45}
            strokeDasharray="1.2 0.8" vectorEffect="non-scaling-stroke"
            pointerEvents="none" />
        ))}
      </g>
      {/* Rampele de acces din planul de situație — pe nivelul pe care stau
          (-1). Până acum nu se desenau deloc, deci nu se vedea intrarea.

          NETĂIATE la conturul planșeului, spre deosebire de tot restul: o rampă
          de acces ÎNCEPE pe pământ nesăpat și intră în excavație, ăsta e rostul
          ei. Tăiată, se vedea doar ultima bucată — cea din subsol — iar de unde
          coboară mașina rămânea în afara desenului (Oana, 20.09.2026). Pe cazul
          ei, 70 % din șanț era în afara conturului. */}
      {accesVizibil && (p.plan.accessRamps ?? []).map((r, i) => (
        <AccessRampView key={`ar${i}`} r={r} />
      ))}
      {/* Interior -1<->-2 ramp: same physical orientation on both levels; a
          single arrow shows travel FROM this level — down to -2 on level -1, up
          to -1 on level -2 (the only way out of the garage is via -1). */}
      {interiorVizibil && (p.plan.rampsInt?.length ? p.plan.rampsInt
                          : p.rampInt ? [p.rampInt] : [])
        .filter((r) => Number.isFinite(r.cx) && Number.isFinite(r.cy))
        .map((r, i) => (
          <g key={`ri${i}`} clipPath={`url(#${clipId})`}>
            <RampIntView r={r} up={p.level === -2}
              label={t(r.dedesubt ? "plan.rampa_dedesubt" : "plan.rampa_spre",
                       { n: p.level === -2 ? -1 : -2 })}
              pick={pick({ kind: "rampInt", level: lvl, i, storey: st })}
              selected={at("rampInt", i)} />
          </g>
        ))}
      {interiorVizibil && (p.plan.rampWalls ?? []).map((w, i) => (
        <RampWallView key={`rw${i}`} w={w} />
      ))}
      {/* the shared access strips go under the bays, so a bay's outline stays crisp */}
      {(p.plan.adaAisles ?? []).map((a, i) => (
        <AdaAisle key={`aa${i}`} a={a}
          pick={pick({ kind: "adaAisle", level: lvl, i, storey: st })}
          selected={at("adaAisle", i)} />
      ))}
      {p.plan.spaces.map((s, i) => (
        <Bay key={`s${i}`} s={s}
          pick={pick({ kind: "stall", level: lvl, i, storey: st })}
          selected={at("stall", i)} />
      ))}
      <RoomsLayer rooms={p.rooms}
        pickOf={(i) => pick({ kind: "room", level: lvl, i, storey: st })}
        selected={sel !== null && sel.kind === "room" ? sel.i : null} />
      {p.showNumbers && p.plan.spaces.map((s: any, i: number) => (
        <StallNumber key={`n${i}`} s={s} n={s.n ?? i + 1} />
      ))}
    </g>
  );
}
